import type { OrgAvailableTypeSchema, Schema } from 'types/schema';

// Generic over the schema shape so callers passing the org-available wire type
// (`OrgAvailableTypeSchema` — the three ownership fields optional) get honest
// types without weakening the common `Schema[]` path: `T` defaults to `Schema`,
// so every existing call site's types are unchanged (this fn never reads the
// ownership fields). Constraint: `OrgAvailableTypeSchema` carries every field
// this weighting logic touches.
export interface SchemaItemResult<T extends OrgAvailableTypeSchema = Schema> {
  schema: T;
  weightScore: number;
  matchedPerspectives: string[];
  matchedUseCases: string[];
}

type Intermediate<T extends OrgAvailableTypeSchema = Schema> = {
  schema: T;
  baseWeight: number;
  weightScore: number;
  matchedPerspectives: string[];
  matchedUseCases: string[];
};

/**
 * Returns all candidate schemas (wrapped as SchemaItemResult) that meet these criteria:
 *
 *   1. If `rootTypeSystemName` is provided, only schemas whose type is that root (or a descendant) are considered.
 *
 *   2. For each candidate C (non‐abstract, in allowedByType), inspect C.perspectives[]:
 *        • Determine an effective list of perspective names to filter by:
 *            – If `perspectiveSystemNames` was passed explicitly in options, use that list.
 *            – Else if `parentSystemName` is provided, derive a list from that parent schema and all its descendants.
 *            – Otherwise, no filtering at this stage (use all of C.perspectives).
 *        • Filter C.perspectives[] down to those whose system_name is in `effectivePerspectives` (if defined).
 *        • If `enforcePerspectiveMatch = true` and the filtered list is empty, drop C immediately.
 *        • If `assigneeSystemName` is provided, for each kept perspective P on C, check P.assignee_filter:
 *            – If P.assignee_filter is empty, P matches.
 *            – Otherwise, if any `{ property: "type", value: X }` matches `assigneeSystemName === X`,
 *              or any `{ property: "label", value: Y }` matches `assigneeNodeLabel === Y`, then P matches.
 *            – If P matches, record P.system_name in `matchedPerspectives` and stop checking further perspectives.
 *        • If no `matchedPerspectives` on C and `enforcePerspectiveMatch = true`, drop C.
 *        • If `assigneeSystemName` is NOT provided, treat all filtered `candidatePerspectives` as `matchedPerspectives`.
 *
 *   3. Next, if `useCaseSystemNames` is provided (non‐empty), intersect C.use_case_tags with that set. Each match = +10 points.
 *        – If `enforceUseCaseMatch = true` and zero `matchedUseCases`, drop C.
 *
 *   4. Compute "perspectiveWeight" for C:
 *        • If `matchedPerspectives` includes 'center_parent', weight = 200.
 *        • Else if includes 'center_responsible', weight = 100.
 *        • Else if includes 'center_center', weight = 300.
 *        • Otherwise, weight = 0.
 *
 *   5. Compute "useCaseWeight" = `matchedUseCases.length * 10` (no additional bonus).
 *        – If `enforceUseCaseMatch = true` and zero `matchedUseCases`, drop C.
 *
 *   6. Total `baseWeight = perspectiveWeight + useCaseWeight`.
 *
 *   7. **Hierarchical Weight Adjustment**: For each schema, look at its own `center_parent` perspective's
 *      `assignee_filter` rules (matching other surviving schemas). Build a DAG of parent→child. Then process
 *      in topological order: for each child, distinguish between natural parent-child relationships with
 *      the assignee vs. competing relationships, boosting natural children and penalizing competitors.
 *
 *   8. Sort survivors by descending `weightScore`, tie-break by `schema.name`.
 *   9. Filter out any where `schema.is_hidden === true`.
 *
 * @param allSchemas  The complete array of Schema objects.
 * @param options
 *   • assigneeSystemName?: string
 *   • perspectiveSystemNames?: string[]
 *   • parentSystemName?: string
 *   • rootTypeSystemName?: string
 *   • useCaseSystemNames?: string[]
 *   • enforcePerspectiveMatch?: boolean
 *   • enforceUseCaseMatch?: boolean
 *
 * @returns A sorted array of SchemaItemResult.
 */
export function getEligibleSchemaObjects<
  T extends OrgAvailableTypeSchema = Schema
>(
  allSchemas: T[],
  options?: {
    assigneeSystemName?: string;
    /**
     * node_label of the assignee. Lets callers resolve assignee-based perspective
     * matching when the assignee's own schema is NOT among `allSchemas` (e.g. the
     * parent sphere's type isn't a deployed-approach "provided" type). When omitted,
     * the label is read from the assignee schema in `allSchemas`.
     */
    assigneeNodeLabel?: string;
    perspectiveSystemNames?: string[];
    parentSystemName?: string;
    rootTypeSystemName?: string;
    useCaseSystemNames?: string[];
    enforcePerspectiveMatch?: boolean;
    enforceUseCaseMatch?: boolean;
  }
): SchemaItemResult<T>[] {
  // 1) Destructure options + defaults
  const {
    assigneeSystemName,
    assigneeNodeLabel: assigneeNodeLabelOption,
    perspectiveSystemNames,
    parentSystemName,
    rootTypeSystemName,
    useCaseSystemNames,
    enforcePerspectiveMatch = false,
    enforceUseCaseMatch = false,
  } = options || {};

  // Normalize `useCaseSystemNames` → if it's an empty array, treat as "not provided"
  const hasUseCaseFilter = Array.isArray(useCaseSystemNames) && useCaseSystemNames.length > 0;
  const effectiveUseCaseSystemNames: string[] | undefined = hasUseCaseFilter ? useCaseSystemNames : undefined;

  // Build quick-lookup maps
  const systemNameToSchema = new Map<string, T>();
  allSchemas.forEach((s) => {
    systemNameToSchema.set(s.system_name, s);
  });

  // Determine assigneeNodeLabel if applicable
  let assigneeNodeLabel: string | null = assigneeNodeLabelOption ?? null;
  if (assigneeSystemName) {
    const aSchema = systemNameToSchema.get(assigneeSystemName);
    if (aSchema) {
      assigneeNodeLabel = aSchema.node_label;
    } else if (assigneeNodeLabel == null) {
      // The assignee (e.g. the parent sphere's own type) isn't among the candidate
      // schemas — common when it isn't a deployed-approach "provided" type. We need
      // its node_label to match label-based perspective filters, so callers may pass
      // `assigneeNodeLabel` explicitly; only bail when neither is available.
      console.log(
        `Assignee type '${assigneeSystemName}' not found and no assigneeNodeLabel provided → returning empty array.`
      );
      return [];
    }
    // else: assignee schema absent but the caller supplied assigneeNodeLabel — use it.
  } else {
    console.log('No assigneeSystemName provided → skipping assignee-based perspective matching');
  }

  // 2) Build "allowedByType" from rootTypeSystemName (if provided)
  const allowedByType = new Set<string>();
  if (rootTypeSystemName) {
    const rootSchema = systemNameToSchema.get(rootTypeSystemName);
    if (!rootSchema) {
      console.log(`rootTypeSystemName '${rootTypeSystemName}' not found → returning empty array.`);
      return [];
    }
    // Build parent→children map
    const parentChildrenMap = new Map<string, T[]>();
    allSchemas.forEach((s) => {
      if (s.parent_schema_id) {
        const arr = parentChildrenMap.get(s.parent_schema_id) || [];
        arr.push(s);
        parentChildrenMap.set(s.parent_schema_id, arr);
      }
    });
    // Depth-first traversal under rootSchema
    const visited = new Set<string>();
    const stack: T[] = [rootSchema];
    while (stack.length) {
      const curr = stack.pop()!;
      if (visited.has(curr.schema_id)) continue;
      visited.add(curr.schema_id);
      if (!curr.is_abstract) {
        allowedByType.add(curr.schema_id);
      }
      (parentChildrenMap.get(curr.schema_id) || []).forEach((c) => stack.push(c));
    }
  } else {
    allSchemas.forEach((s) => {
      if (!s.is_abstract) {
        allowedByType.add(s.schema_id);
      }
    });
  }

  // 3) Build set of allowed use cases (if any)
  const allowedUseCases = new Set<string>(effectiveUseCaseSystemNames || []);

  // 4) Determine effectivePerspectives list and whether derived from parent
  let effectivePerspectives: string[] | undefined = undefined;
  let derivedFromParent = false;

  if (perspectiveSystemNames && perspectiveSystemNames.length > 0) {
    // Use explicit list
    effectivePerspectives = perspectiveSystemNames;
  } else if (parentSystemName) {
    // Derive from parentSystemName: collect that schema and all its descendants' perspectives
    const parentSchema = systemNameToSchema.get(parentSystemName);
    if (!parentSchema) {
      return [];
    }
    // Build parent→children map
    const parentChildrenMap = new Map<string, T[]>();
    allSchemas.forEach((s) => {
      if (s.parent_schema_id) {
        const arr = parentChildrenMap.get(s.parent_schema_id) || [];
        arr.push(s);
        parentChildrenMap.set(s.parent_schema_id, arr);
      }
    });
    // DFS from parentSchema to collect descendants
    const visited = new Set<string>();
    const stack: T[] = [parentSchema];
    const perspectiveSet = new Set<string>();
    while (stack.length) {
      const curr = stack.pop()!;
      if (visited.has(curr.schema_id)) continue;
      visited.add(curr.schema_id);
      // Add all perspectives of curr
      (curr.perspectives || []).forEach((p) => perspectiveSet.add(p.system_name));
      (parentChildrenMap.get(curr.schema_id) || []).forEach((c) => stack.push(c));
    }
    effectivePerspectives = Array.from(perspectiveSet);
    derivedFromParent = true;
  } else {
    console.log('No perspectiveSystemNames or parentSystemName provided');
  }

  // 5) Evaluate each candidate "C" and compute baseWeight
  const interimResults: Intermediate<T>[] = [];

  for (const candidate of allSchemas) {
    // a) Skip abstract
    if (candidate.is_abstract) {
      continue;
    }
    // b) Skip if not in allowedByType
    if (!allowedByType.has(candidate.schema_id)) {
      continue;
    }

    // c) Filter candidatePerspectives by effectivePerspectives (if defined)
    let candidatePerspectives = candidate.perspectives || [];
    if (effectivePerspectives && effectivePerspectives.length > 0) {
      candidatePerspectives = candidatePerspectives.filter((p) => effectivePerspectives.includes(p.system_name));
    }
    // Immediately drop if enforcePerspectiveMatch and no perspectives remain
    if (enforcePerspectiveMatch && candidatePerspectives.length === 0) {
      continue;
    }

    // d) Determine matchedPerspectives
    const matchedPerspectives: string[] = [];
    if (!assigneeSystemName) {
      // No assignee → everything in candidatePerspectives counts
      matchedPerspectives.push(...candidatePerspectives.map((p) => p.system_name));
    } else {
      // assigneeSystemName provided → check assignee_filter per perspective
      for (const cp of candidatePerspectives) {
        const filters = cp.assignee_filter || [];
        if (filters.length === 0) {
          matchedPerspectives.push(cp.system_name);
          break;
        }
        let perspectiveMatches = false;
        for (const { property, value } of filters) {
          if (property === 'type' && assigneeSystemName === value) {
            perspectiveMatches = true;
            break;
          }
          if (property === 'label' && assigneeNodeLabel === value) {
            perspectiveMatches = true;
            break;
          }
        }
        if (perspectiveMatches) {
          matchedPerspectives.push(cp.system_name);
          break;
        }
      }
      if (enforcePerspectiveMatch && matchedPerspectives.length === 0) {
        continue;
      }
    }

    // e) Use-case matching (only if effectiveUseCaseSystemNames is non-empty)
    const candidateUseCases = candidate.use_case_tags || [];

    const matchedUseCases = effectiveUseCaseSystemNames
      ? candidateUseCases.filter((uc) => {
          const isMatch = allowedUseCases.has(uc);

          return isMatch;
        })
      : [];

    if (enforceUseCaseMatch && matchedUseCases.length === 0 && effectiveUseCaseSystemNames) {
      continue;
    }

    // f) Compute perspectiveWeight:
    //    • If 'center_parent' in matchedPerspectives → 200
    //    • Else if 'center_responsible' → 100
    //    • Else if 'center_center' → 300
    //    • Else → 0

    let perspectiveWeight = 0;
    if (matchedPerspectives.includes('center_parent')) {
      perspectiveWeight = 200;
    } else if (matchedPerspectives.includes('center_responsible')) {
      perspectiveWeight = 100;
    } else if (matchedPerspectives.includes('center_center')) {
      perspectiveWeight = 300;
    }

    // g) Compute useCaseWeight = +10 per matched use case (no bonus)

    const useCaseWeight = matchedUseCases.length * 10;

    // h) Combine to get baseWeight
    const baseWeight = perspectiveWeight + useCaseWeight;

    interimResults.push({
      schema: candidate,
      baseWeight,
      weightScore: baseWeight, // initialize with baseWeight; will adjust later
      matchedPerspectives,
      matchedUseCases,
    });
  }

  // Build lookup maps from system_name and node_label to Intermediate
  const systemNameToIntermediate = new Map<string, Intermediate<T>>();
  const nodeLabelToIntermediate = new Map<string, Intermediate<T>>();
  interimResults.forEach((item) => {
    systemNameToIntermediate.set(item.schema.system_name, item);
    nodeLabelToIntermediate.set(item.schema.node_label, item);
  });

  // 6) Build parent→child graph based on each schema's own center_parent perspective
  const childToParents = new Map<string, Set<string>>(); // childId → set of parentIds
  const parentToChildren = new Map<string, Set<string>>(); // parentId → set of childIds

  // Initialize sets
  interimResults.forEach((cand) => {
    childToParents.set(cand.schema.schema_id, new Set());
    parentToChildren.set(cand.schema.schema_id, new Set());
  });

  // Populate edges
  interimResults.forEach((child) => {
    const cpPerspective = child.schema.perspectives?.find((p) => p.system_name === 'center_parent');
    if (!cpPerspective) return;

    for (const filter of cpPerspective.assignee_filter || []) {
      let parentIntermediate: Intermediate<T> | undefined;

      if (filter.property === 'type') {
        parentIntermediate = systemNameToIntermediate.get(filter.value);
      } else if (filter.property === 'label') {
        parentIntermediate = nodeLabelToIntermediate.get(filter.value);
      }

      if (parentIntermediate && parentIntermediate.schema.schema_id !== child.schema.schema_id) {
        const childId = child.schema.schema_id;
        const parentId = parentIntermediate.schema.schema_id;
        childToParents.get(childId)!.add(parentId);
        parentToChildren.get(parentId)!.add(childId);
      }
    }
  });

  // 7) Hierarchical Weight Adjustment using topological order
  // Compute in-degree (number of parents) for each node
  const inDegree = new Map<string, number>();
  interimResults.forEach((item) => {
    const id = item.schema.schema_id;
    inDegree.set(id, childToParents.get(id)!.size);
  });

  // Initialize queue with nodes having in-degree 0 (roots)
  const queue: string[] = [];
  inDegree.forEach((deg, id) => {
    if (deg === 0) queue.push(id);
  });

  while (queue.length) {
    const parentId = queue.shift()!;

    // Process each child of this parent
    const children = parentToChildren.get(parentId)!;
    children.forEach((childId) => {
      const childItem = interimResults.find((r) => r.schema.schema_id === childId)!;
      const childUseCount = childItem.matchedUseCases.length;

      // Get all parent relationships for this child
      const parentIds = Array.from(childToParents.get(childId)!);

      // **NEW LOGIC**: Categorize parent relationships
      let assigneeAsDirectParent = false;
      let assigneeAsSelfParent = false;
      const otherParents: string[] = [];

      parentIds.forEach((pid) => {
        const pItem = interimResults.find((r) => r.schema.schema_id === pid);
        if (!pItem) return;

        if (assigneeSystemName && pItem.schema.system_name === assigneeSystemName) {
          if (childItem.schema.system_name === assigneeSystemName) {
            // Assignee can be parent of itself (self-referential)
            assigneeAsSelfParent = true;
          } else {
            // Assignee is parent of a different schema (natural parent-child)
            assigneeAsDirectParent = true;
          }
        } else {
          // Other non-assignee parents
          otherParents.push(pid);
        }
      });

      if (assigneeAsDirectParent && !assigneeAsSelfParent) {
        // BOOST: Natural parent-child relationship (center_pillar → center_priority)
        const oldScore = childItem.weightScore;
        childItem.weightScore = childItem.baseWeight + 100;
      } else if (assigneeAsSelfParent && assigneeAsDirectParent) {
        // MIXED: Assignee can be both parent of itself AND parent of this child
        // In this case, apply normal hierarchical logic but exclude self-parent from calculation
        const assigneeItem = interimResults.find((r) => r.schema.system_name === assigneeSystemName);
        let minParentWeight = Infinity;

        // Only consider the assignee's score as a "competing" parent, not as natural parent
        if (assigneeItem) {
          minParentWeight = Math.min(minParentWeight, assigneeItem.weightScore);
        }

        otherParents.forEach((pid) => {
          const pItem = interimResults.find((r) => r.schema.schema_id === pid);
          if (!pItem) return;
          const parentUseCount = pItem.matchedUseCases.length;
          if (childUseCount > 0 && parentUseCount === 0) return;
          minParentWeight = Math.min(minParentWeight, pItem.weightScore);
        });

        if (minParentWeight !== Infinity && childItem.weightScore >= minParentWeight) {
          const oldScore = childItem.weightScore;
          childItem.weightScore = minParentWeight - 1;
        }
      } else if (assigneeAsSelfParent && !assigneeAsDirectParent) {
        // SELF-REFERENTIAL: Assignee is parent of itself only
        const assigneeItem = interimResults.find((r) => r.schema.system_name === assigneeSystemName);
        if (assigneeItem && childItem.weightScore >= assigneeItem.weightScore) {
          const oldScore = childItem.weightScore;
          childItem.weightScore = assigneeItem.weightScore - 1;
        }
      } else {
        // NORMAL: Standard hierarchical adjustment for non-assignee relationships
        let minParentWeight = Infinity;
        otherParents.forEach((pid) => {
          const pItem = interimResults.find((r) => r.schema.schema_id === pid);
          if (!pItem) return;
          const parentUseCount = pItem.matchedUseCases.length;
          if (childUseCount > 0 && parentUseCount === 0) return;
          minParentWeight = Math.min(minParentWeight, pItem.weightScore);
        });

        if (minParentWeight !== Infinity && childItem.weightScore >= minParentWeight) {
          const oldScore = childItem.weightScore;
          childItem.weightScore = minParentWeight - 1;
        }
      }

      // Decrement in-degree for topological processing
      const deg = inDegree.get(childId)! - 1;
      inDegree.set(childId, deg);
      if (deg === 0) {
        queue.push(childId);
      }
    });
  }

  interimResults
    .slice()
    .sort((a, b) => b.weightScore - a.weightScore)
    .forEach((r) => {
      const parentIds = childToParents.get(r.schema.schema_id)!;
      const parentNames = Array.from(parentIds).map((pid) => {
        const p = interimResults.find((ir) => ir.schema.schema_id === pid);
        return p?.schema.system_name ?? pid;
      });
    });

  // 8) Sort descending by final weightScore, then by schema.name

  interimResults.sort((a, b) => {
    if (b.weightScore !== a.weightScore) {
      return b.weightScore - a.weightScore;
    }
    return a.schema.name.localeCompare(b.schema.name);
  });

  // 9) Filter out hidden schemas from results
  const visibleResults = interimResults.filter((r) => !r.schema.is_hidden);

  return visibleResults.map((r) => ({
    schema: r.schema,
    weightScore: r.weightScore,
    matchedPerspectives: r.matchedPerspectives,
    matchedUseCases: r.matchedUseCases,
  }));
}
